/**
 * The end of a paged wall: the next page's place, or the line after the last one.
 *
 * The next page is asked for while the end is still about a row below the fold (`useLoadNearEnd`);
 * „Pokaż starsze nagrania" is the same request for a keyboard, or for a scroll that outran it. Its
 * wait stands in a row of tile shapes where the page will land — nothing above it moves, and the
 * row's box is there from the ask, before the bars have earned their place.
 */

import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { OlderPages } from '../lib/useClipCollection';
import { useLoadNearEnd } from '../lib/useLoadNearEnd';

import styles from './ClipOlderPages.module.css';
import { ClipTileRowSkeleton } from './ClipTileGrid';

const SKELETON_TILES = 6;

export function ClipOlderPages({ older }: { readonly older: OlderPages }) {
  const { t } = useTranslation();
  const end = useRef<HTMLDivElement>(null);
  const gate = useSkeletonGate(older.pending);
  useLoadNearEnd(end, older.exists && !older.pending && !older.failed, older.load);

  if (!older.exists) return <p className={styles.end}>{t('web:clipWall.end')}</p>;

  return (
    <div aria-busy={gate.waiting} className={styles.older} ref={end}>
      {gate.waiting ? (
        <>
          <span className={hidden.srOnly}>{t('web:clipWall.moreLoading')}</span>
          <ClipTileRowSkeleton drawn={gate.skeleton} oneRow tiles={SKELETON_TILES} />
        </>
      ) : older.failed ? (
        <p className={styles.notice} role="alert">
          {t('web:clipWall.moreFailed')}{' '}
          <button className={inlineButton.inlineTextButton} onClick={older.load} type="button">
            {t('web:clipWall.retry')}
          </button>
        </p>
      ) : (
        <button className={`${String(ghost.ghost)} ${String(styles.olderButton)}`} onClick={older.load} type="button">
          {t('web:clipWall.more')}
        </button>
      )}
    </div>
  );
}
